import { useState } from 'react';
import { toast } from 'react-toastify';
import { FeedChannel, SharedImage } from '../../types';
import useGetPostedImages from './useGetPostedImages';
import useFollowUser from '../useFollowUser';

/**
 * Hook for fetching and filtering images from followed users
 * Returns images posted only by users that the current user follows
 */
const useFollowedUsersImages = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the getPostedImages function from the useGetPostedImages hook
  const getPostedImages = useGetPostedImages();

  // Get followed user IDs from the useFollowUser hook
  const { isFollowing } = useFollowUser();

  /**
   * Fetches all images and filters them to only include those from followed users
   * @param params - Parameters for fetching images
   * @returns Promise with filtered images from followed users
   */
  const getFollowedUsersImages = async (): Promise<SharedImage[]> => {
    setLoading(true);
    setError(null);

    const sort = 'hot';
    const channelName = 'all';
    const channelList: FeedChannel[] = [];

    try {
      // Fetch all images using the getPostedImages function
      const allImages = await getPostedImages({
        sort,
        channelName,
        channelList,
      });

      // Filter images to only include those from users the current user follows
      const filteredImages = allImages.filter((image) => {
        // Check if the image has a userId field (new way) or user.id field (old way)
        const imageUserId = image.userId || image.user?.id;

        // Return true if the user follows the image creator
        return imageUserId ? isFollowing(imageUserId) : false;
      });

      return filteredImages;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch images from followed users';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching followed users images:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Combines images from followed users and all images, showing one followed user image for every third image
   * @param allImages - Array of all images
   * @param followedUserImages - Array of images from followed users
   * @returns Combined array with followed user images every third position, followed by any remaining images, with duplicates removed
   */
  const addFollowedUserImagesToAllImages = (
    allImages: SharedImage[],
    followedUserImages: SharedImage[],
  ): SharedImage[] => {
    // Create a new array to hold the combined images
    const result: SharedImage[] = [];

    const shuffledFollowedUserImages = followedUserImages.sort(
      () => Math.random() - 0.5,
    );

    // Copy all images to start with
    result.push(...allImages);
    
    // Calculate how many followed user images we can insert
    // We can insert at positions 2, 5, 8, ... (every third position)
    const maxInsertions = Math.min(
      shuffledFollowedUserImages.length,
      Math.floor((allImages.length + 2) / 3)
    );
    
    // Insert followed user images at every third position
    for (let i = 0; i < maxInsertions; i++) {
      const insertPosition = i * 3 + 2; // Insert at positions 2, 5, 8, ...
      if (insertPosition <= result.length) {
        result.splice(insertPosition, 0, shuffledFollowedUserImages[i]);
      }
    }
    
    // Add any remaining followed user images that weren't inserted
    if (shuffledFollowedUserImages.length > maxInsertions) {
      result.push(...shuffledFollowedUserImages.slice(maxInsertions));
    }

    // Remove any duplicates by ID
    return result.filter(
      (image, index, self) =>
        image && index === self.findIndex((t) => t?.id === image?.id),
    );
  };

  return {
    getFollowedUsersImages,
    addFollowedUserImagesToAllImages,
    loading,
    error,
  };
};

export default useFollowedUsersImages;
